import {
  isEmptyFollowUp,
  type FollowUp,
  type Quiz,
  type Screen,
  type Theme,
} from '@floorpoll/shared';

/**
 * Whether the builder is holding anything the server has not accepted yet.
 *
 * Field by field rather than `JSON.stringify` on the two objects, because
 * stringify answers a different question: it compares key order, and it cannot
 * tell an absent optional field from one explicitly set to the value it
 * defaults to. Both differences are reachable from the builder —
 *
 *   - `ThemeEditor` writes `{ ...theme, logoUrl: url }`, which appends the key
 *     to a poll saved before a logo was ever set. Removing the logo then leaves
 *     `logoUrl: null` where the poll had no such key at all.
 *   - Ticking "let people pick more than one answer" and unticking it leaves
 *     `multiSelect: false` where the question had no such key.
 *
 * — and either would strand the bar on "Unsaved changes" for a poll with
 * nothing left to save. An indicator that cries wolf is worse than none, since
 * the whole reason it exists is to make a quiet Save button legible.
 */

const same = <T>(a: T[], b: T[], eq: (x: T, y: T) => boolean) =>
  a.length === b.length && a.every((item, index) => eq(item, b[index] as T));

/** Absent, blank, and every-field-blank all mean the same thing: no follow-up. */
function sameFollowUp(a: FollowUp | null | undefined, b: FollowUp | null | undefined) {
  if (isEmptyFollowUp(a) || isEmptyFollowUp(b)) return isEmptyFollowUp(a) && isEmptyFollowUp(b);
  return (
    (a?.message ?? '') === (b?.message ?? '') &&
    (a?.url ?? '') === (b?.url ?? '') &&
    (a?.linkLabel ?? '') === (b?.linkLabel ?? '')
  );
}

function sameScreens(a: Screen[], b: Screen[]) {
  return same(
    a,
    b,
    (screenA, screenB) =>
      screenA.screenId === screenB.screenId &&
      same(
        screenA.questions,
        screenB.questions,
        (questionA, questionB) =>
          questionA.questionId === questionB.questionId &&
          questionA.text === questionB.text &&
          (questionA.multiSelect === true) === (questionB.multiSelect === true) &&
          same(
            questionA.options,
            questionB.options,
            (optionA, optionB) =>
              optionA.optionId === optionB.optionId &&
              optionA.text === optionB.text &&
              sameFollowUp(optionA.followUp, optionB.followUp),
          ),
      ),
  );
}

function sameTheme(a: Theme, b: Theme) {
  return (
    a.primaryColor === b.primaryColor &&
    a.accentColor === b.accentColor &&
    a.backgroundColor === b.backgroundColor &&
    a.textColor === b.textColor &&
    (a.logoUrl ?? null) === (b.logoUrl ?? null) &&
    (a.backgroundImageUrl ?? null) === (b.backgroundImageUrl ?? null) &&
    // Absent reads as 1 everywhere else too — see the web client's questionGrid.
    (a.questionColumns ?? 1) === (b.questionColumns ?? 1)
  );
}

export function hasUnsavedChanges(
  saved: Quiz,
  title: string,
  screens: Screen[],
  theme: Theme,
): boolean {
  // Trimmed, because that is what `save` sends: without it a trailing space
  // would read as unsaved work that saving could never clear.
  return (
    title.trim() !== saved.title ||
    !sameScreens(screens, saved.screens) ||
    !sameTheme(theme, saved.theme)
  );
}
